import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const menuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const megaMenuContainerRef = useRef<HTMLDivElement>(null);
  const productsButtonRef = useRef<HTMLButtonElement>(null);

  // URL'den dil algılama
  const isEnglishPage = location.pathname.startsWith('/en');

  const products = [
    {
      slug: 'kapali-kabin',
      name: isEnglishPage ? 'Fully Enclosed Fiber Laser' : 'Kapalı Kabin Fiber Lazer',
      description: isEnglishPage ? 'Enclosed CNC Cutting Machines' : 'Kapalı Kabin CNC Kesim Makineleri',
      mainImage: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/48db2d4eadb135f04b847468fe9d54fe.jpeg',
    },
    {
      slug: 'gezer-kabin',
      name: isEnglishPage ? 'Enclosed Gantry Fiber Laser' : 'Gezer Kabin Fiber Lazer',
      description: isEnglishPage ? 'Mobile Cabin Systems' : 'Hareketli Kabin Sistemleri',
      mainImage: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/87536d91e938e8feb153e654c7ee1710.png',
    },
    {
      slug: 'boru-profil-kesim',
      name: isEnglishPage ? 'Tube and Profile Fiber Laser' : 'Boru ve Profil Fiber Lazer',
      description: isEnglishPage ? 'Tube And Profile Cutting Solutions' : 'Boru ve Profil Kesim Çözümleri',
      mainImage: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/4e215bb22311963bc7f79b028b56cdde.png',
    }
  ];

  const navigation = [
    { name: t('nav.cuttingThickness'), href: isEnglishPage ? '/en/cutting-thickness' : '/kesim-kalinliklari', isScroll: false },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleLanguageChange = (lang: string) => {
    const routes: Record<string, string> = {
      'tr': '/',
      'en': '/en',
      'ru': '/ru',
      'us': '/us'
    };
    
    window.REACT_APP_NAVIGATE(routes[lang] || '/');
    i18n.changeLanguage(lang);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  const scrollToCTA = () => {
    // Her zaman ana sayfaya git
    if (i18n.language === 'en') {
      navigate('/en');
    } else {
      navigate('/');
    }
    
    // Sayfa yüklendikten sonra forma scroll yap
    setTimeout(() => {
      const ctaElement = document.getElementById('cta-section');
      if (ctaElement) {
        const yOffset = -80;
        const y = ctaElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 300);
    
    setMobileMenuOpen(false);
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(i18n.language === 'en' ? '/en' : '/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleKatalogClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(i18n.language === 'en' ? '/en/catalog' : '/katalog');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 0);
  };

  const handleNavigationClick = (item: typeof navigation[0]) => {
    if (item.href === '/fuarlar') {
      navigate(i18n.language === 'en' ? '/en/fairs' : '/fuarlar');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }, 0);
      setMobileMenuOpen(false);
    } else if (item.href === '/kesim-kalinliklari' || item.href === '/en/cutting-thickness') {
      navigate(item.href);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }, 0);
      setMobileMenuOpen(false);
    }
  };

  const handleMouseEnterProducts = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    setProductsMenuOpen(true);
  };

  const handleMouseLeaveProducts = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setProductsMenuOpen(false);
    }, 300);
  };

  const handleProductsButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setProductsMenuOpen(!productsMenuOpen);
  };

  // Overlay'e tıklandığında mega menüyü kapat
  const handleOverlayClick = (e: React.MouseEvent) => {
    // Sadece overlay'in kendisine tıklandığında kapat (içerideki content'e değil)
    if (e.target === e.currentTarget) {
      setProductsMenuOpen(false);
    }
  };

  // Mega menü dışına tıklandığında kapat
  useEffect(() => {
    if (!productsMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Mega menü container'ı veya ürünler butonunu kontrol et
      const isInsideMegaMenu = megaMenuContainerRef.current?.contains(target);
      const isInsideButton = productsButtonRef.current?.contains(target);
      
      // Eğer ikisinin de dışındaysa menüyü kapat
      if (!isInsideMegaMenu && !isInsideButton) {
        setProductsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, { capture: true });
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, { capture: true });
    };
  }, [productsMenuOpen]);

  // Sayfa değiştiğinde mega menüyü kapat
  useEffect(() => {
    setProductsMenuOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname]);

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '100%' }}>
        <div className="flex h-20 items-center justify-between gap-4">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to={i18n.language === 'en' ? '/en' : '/'} onClick={handleLogoClick} className="flex items-center cursor-pointer">
              <img 
                src={isEnglishPage 
                  ? "https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/5cdb8acd1a926456732ace3d319bea51.png"
                  : "https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/d06c1341d4e455ad1900d95909ec2b43.png"
                }
                alt={isEnglishPage ? "AJAN LASER" : "AJAN LAZER"}
                className="h-10 sm:h-12 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation - Logo'ya yakın - Görünür 1280px+ */}
          <div className="hidden min-[1280px]:flex min-[1280px]:items-center min-[1280px]:gap-5 ml-8">
            {/* Ürünler Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleMouseEnterProducts}
              onMouseLeave={handleMouseLeaveProducts}
            >
              <button
                ref={productsButtonRef}
                onClick={handleProductsButtonClick}
                className="text-[22px] font-semibold transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-[#D62828] flex items-center gap-1"
              >
                {isEnglishPage ? 'Products' : 'Ürünler'}
                <i className={`ri-arrow-down-s-line text-xl transition-transform ${productsMenuOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Mega Menu - Ortalanmış ve Genişletilmiş */}
              {productsMenuOpen && (
                <div 
                  className="fixed left-0 right-0 top-20 pt-4 flex justify-center animate-fadeIn z-50"
                  onClick={handleOverlayClick}
                >
                  <div 
                    ref={megaMenuContainerRef}
                    className="bg-white rounded-2xl shadow-2xl border-t-4 border-[#D62828] p-8 w-full max-w-[1600px] mx-4"
                    onMouseEnter={handleMouseEnterProducts}
                    onMouseLeave={handleMouseLeaveProducts}
                  >
                    <div className="grid grid-cols-3 gap-10">
                      {products.map((product) => (
                        <Link
                          key={product.slug}
                          to={isEnglishPage ? `/en/products/${product.slug}` : `/products/${product.slug}`}
                          onClick={() => {
                            setProductsMenuOpen(false);
                            setTimeout(() => {
                              window.scrollTo({ top: 0, behavior: 'instant' });
                            }, 0);
                          }}
                          className="group relative bg-white rounded-2xl border border-gray-100 hover:border-[#D62828]/30 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden cursor-pointer"
                        >
                          {/* Ürün Görseli */}
                          <div className="bg-gray-50 p-6 overflow-hidden">
                            <div className="h-[340px] w-full overflow-hidden rounded-xl">
                              <img
                                src={product.mainImage}
                                alt={product.name}
                                className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                              />
                            </div>
                          </div>

                          {/* Ürün Bilgisi */}
                          <div className="p-6">
                            <h3 className="text-2xl font-bold text-gray-900 text-center mb-3 group-hover:text-[#D62828] transition-colors">
                              {product.name}
                            </h3>
                            <p className="text-base text-gray-600 text-center">
                              {product.description}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigationClick(item)}
                className={`text-[22px] font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  isActive(item.href)
                    ? 'text-[#D62828]'
                    : 'text-gray-700 hover:text-[#D62828]'
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>

          {/* Desktop Action Buttons - Görünür 1280px+ */}
          <div className="hidden min-[1280px]:flex min-[1280px]:items-center min-[1280px]:gap-2 ml-auto">
            <button
              onClick={scrollToCTA}
              className="px-4 py-2.5 bg-[#D62828] text-white text-[19px] font-medium rounded-lg hover:bg-[#B91C1C] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-text-line mr-1.5"></i>
              {t('nav.getQuote')}
            </button>
            <a
              href={i18n.language === 'en' ? '/en/catalog' : '/katalog'}
              onClick={handleKatalogClick}
              className="px-4 py-2.5 bg-gradient-to-r from-[#F77F00] to-[#FCBF49] text-white text-[19px] font-medium rounded-lg hover:from-[#E67700] hover:to-[#F0B840] transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-2-line mr-1.5"></i>
              {t('nav.eCatalog')}
            </a>
            <a
              href={isEnglishPage ? "tel:+904442526" : "tel:4442526"}
              className="px-4 py-2.5 bg-gray-900 text-white text-[19px] font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-line mr-1.5"></i>
              {isEnglishPage ? '+90 444 25 26' : '444 25 26'}
            </a>
            {!isEnglishPage && (
              <a
                href="https://shop.ajanlazer.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2.5 bg-gradient-to-r from-[#06B6D4] to-[#0891B2] text-white text-[19px] font-medium rounded-lg hover:from-[#0891B2] hover:to-[#0E7490] transition-all shadow-md hover:shadow-lg whitespace-nowrap"
              >
                <i className="ri-shopping-cart-2-line mr-1.5"></i>
                {t('nav.onlineOrder')}
              </a>
            )}
            
            {/* Region Selector - 4 Options */}
            <div className="relative group">
              <button className="w-11 h-11 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <img 
                  src={isEnglishPage ? 'https://flagcdn.com/w40/gb.png' : 'https://flagcdn.com/w40/tr.png'}
                  alt={isEnglishPage ? 'English' : 'Türkçe'}
                  className="w-7 h-5 rounded"
                />
              </button>
              
              {/* Dropdown */}
              <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                <button
                  onClick={() => handleLanguageChange('tr')}
                  className="w-full px-4 py-2 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                >
                  <img src="https://flagcdn.com/w40/tr.png" alt="Türkiye" className="w-6 h-4 rounded" />
                  <span className="text-base">Türkiye (TR)</span>
                </button>
                <button
                  onClick={() => handleLanguageChange('en')}
                  className="w-full px-4 py-2 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                >
                  <img src="https://flagcdn.com/w40/gb.png" alt="Global" className="w-6 h-4 rounded" />
                  <span className="text-base">Global (EN)</span>
                </button>
                <button
                  onClick={() => handleLanguageChange('us')}
                  className="w-full px-4 py-2 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                >
                  <img src="https://flagcdn.com/w40/us.png" alt="USA" className="w-6 h-4 rounded" />
                  <span className="text-base">USA (US)</span>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile menu button - Görünür 0-1280px */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="min-[1280px]:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="min-[1280px]:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col gap-4">
              {/* Mobile Ürünler */}
              <div className="border-b border-gray-200 pb-4">
                <button
                  onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                  className="text-[19px] font-medium text-gray-700 hover:text-[#D62828] transition-colors text-left cursor-pointer w-full flex items-center justify-between"
                >
                  <span>{isEnglishPage ? 'Products' : 'Ürünler'}</span>
                  <i className={`ri-arrow-down-s-line text-xl transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {mobileProductsOpen && (
                  <div className="mt-4 space-y-3 pl-4">
                    {products.map((product) => (
                      <Link
                        key={product.slug}
                        to={isEnglishPage ? `/en/products/${product.slug}` : `/products/${product.slug}`}
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setMobileProductsOpen(false);
                          setTimeout(() => {
                            window.scrollTo({ top: 0, behavior: 'instant' });
                          }, 0);
                        }}
                        className="block bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <div className="font-semibold text-gray-900 hover:text-[#D62828] mb-1">{product.name}</div>
                        <div className="text-sm text-gray-600">{product.description}</div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navigation.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigationClick(item)}
                  className={`text-[19px] font-medium transition-colors text-left cursor-pointer ${
                    isActive(item.href)
                      ? 'text-[#D62828]'
                      : 'text-gray-700 hover:text-[#D62828]'
                  }`}
                >
                  {item.name}
                </button>
              ))}
              <button
                onClick={scrollToCTA}
                className="px-6 py-3 bg-[#D62828] text-white text-[19px] font-medium rounded-lg hover:bg-[#B91C1C] transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-text-line mr-2"></i>
                {t('nav.getQuote')}
              </button>
              <a
                href={i18n.language === 'en' ? '/en/catalog' : '/katalog'}
                onClick={(e) => {
                  handleKatalogClick(e);
                  setMobileMenuOpen(false);
                }}
                className="px-6 py-3 bg-gradient-to-r from-[#F77F00] to-[#FCBF49] text-white text-[19px] font-medium rounded-lg hover:from-[#E67700] hover:to-[#F0B840] transition-all shadow-md text-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-2-line mr-2"></i>
                {t('nav.eCatalog')}
              </a>
              <a
                href={isEnglishPage ? "tel:+904442526" : "tel:4442526"}
                className="px-6 py-3 bg-gray-900 text-white text-[19px] font-medium rounded-lg hover:bg-gray-800 transition-colors text-center whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                {isEnglishPage ? '+90 444 25 26' : '444 25 26'}
              </a>
              {!isEnglishPage && (
                <a
                  href="https://shop.ajanlazer.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-6 py-3 bg-gradient-to-r from-[#06B6D4] to-[#0891B2] text-white text-[19px] font-medium rounded-lg hover:from-[#0891B2] hover:to-[#0E7490] transition-all shadow-md text-center whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-2-line mr-2"></i>
                  {t('nav.onlineOrder')}
                </a>
              )}
              
              {/* Mobile Region Selector - 4 Options */}
              <div className="border-t border-gray-200 pt-4 space-y-2">
                <button
                  onClick={() => {
                    handleLanguageChange('tr');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center gap-3 cursor-pointer"
                >
                  <img src="https://flagcdn.com/w40/tr.png" alt="Türkiye" className="w-6 h-4 rounded" />
                  <span className="text-base">Türkiye (TR)</span>
                </button>
                <button
                  onClick={() => {
                    handleLanguageChange('en');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center gap-3 cursor-pointer"
                >
                  <img src="https://flagcdn.com/w40/gb.png" alt="Global" className="w-6 h-4 rounded" />
                  <span className="text-base">Global (EN)</span>
                </button>
                <button
                  onClick={() => {
                    handleLanguageChange('us');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center gap-3 cursor-pointer"
                >
                  <img src="https://flagcdn.com/w40/us.png" alt="USA" className="w-6 h-4 rounded" />
                  <span className="text-base">USA (US)</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
